import React from 'react';

import { useTranslation } from 'react-i18next';

import { DotaTagIcon, CsTagIcon, LolTagIcon } from '@icons';

import { useModals } from '@shared/contexts';

import { useQueryTournaments } from '@hooks/api/tournaments';

import { GAME_TYPE, TournamentEvent, FilterGameType } from '@types';

import { Loader } from '@components/loader';
import { ModalName } from '@components/modals/types';

import { CalendarHeader, MobileCalendarHeader } from './components';
import * as Styled from './tournaments-calendar.styled';

const TournamentsCalendar: React.FC = () => {
	const { t } = useTranslation('main');
	const { openModal } = useModals();
	const { data: events = [], isLoading } = useQueryTournaments();

	const today = new Date();
	const [currentMonth, setCurrentMonth] = React.useState(today.getMonth());
	const [currentYear, setCurrentYear] = React.useState(today.getFullYear());
	const [filteredGameType, setFilteredGameType] = React.useState<FilterGameType>(null);

	const months = React.useMemo(
		() => [
			t('january'),
			t('february'),
			t('march'),
			t('april'),
			t('may'),
			t('june'),
			t('july'),
			t('august'),
			t('september'),
			t('october'),
			t('november'),
			t('december')
		],
		[t]
	);

	const daysOfWeek = React.useMemo(() => [t('mo'), t('tw'), t('we'), t('thu'), t('fr'), t('st'), t('sd')], [t]);

	const handlePrevMonth = () => {
		setCurrentMonth(prev => (prev === 0 ? 11 : prev - 1));
		if (currentMonth === 0) {
			setCurrentYear(prev => prev - 1);
		}
	};

	const handleNextMonth = () => {
		setCurrentMonth(prev => (prev === 11 ? 0 : prev + 1));
		if (currentMonth === 11) {
			setCurrentYear(prev => prev + 1);
		}
	};

	const handleFilterClick = (gameType: GAME_TYPE) => {
		setFilteredGameType(prev => (prev === gameType ? null : gameType));
	};

	const daysInMonth = (month: number, year: number) => new Date(year, month + 1, 0).getDate();
	const getFirstDayOfMonth = (month: number, year: number) => {
		const firstDay = new Date(year, month, 1).getDay();
		return firstDay === 0 ? 6 : firstDay - 1;
	};

	const firstDayOfMonth = getFirstDayOfMonth(currentMonth, currentYear);

	const getEventsForDay = (day: number): TournamentEvent[] => {
		const currentDate = new Date(Date.UTC(currentYear, currentMonth, day));

		return events.filter(event => {
			if (!event.startDate || !event.endDate) return false;

			const start = new Date(
				Date.UTC(event.startDate.getUTCFullYear(), event.startDate.getUTCMonth(), event.startDate.getUTCDate())
			);
			const end = new Date(
				Date.UTC(event.endDate.getUTCFullYear(), event.endDate.getUTCMonth(), event.endDate.getUTCDate())
			);

			const isWithinDateRange = currentDate >= start && currentDate <= end;

			if (filteredGameType) {
				return isWithinDateRange && event.gameType === filteredGameType;
			}

			return isWithinDateRange;
		});
	};

	const renderDaysOfWeek = () => {
		return daysOfWeek.map((day, index) => <Styled.DayOfWeek key={index} data-day={day} />);
	};

	const renderDays = () => {
		const totalDays = daysInMonth(currentMonth, currentYear);
		const daysArray = [];

		const todayDate = today.getDate();
		const todayMonth = today.getMonth();
		const todayYear = today.getFullYear();

		for (let i = 0; i < firstDayOfMonth; i++) {
			daysArray.push(<Styled.DayEmpty key={`empty-${i}`} />);
		}

		for (let day = 1; day <= totalDays; day++) {
			const isToday = day === todayDate && currentMonth === todayMonth && currentYear === todayYear;
			const eventsForDay = getEventsForDay(day);

			daysArray.push(
				<Styled.Day key={day} $isToday={isToday}>
					<Styled.DayNum $isToday={isToday}>{day}</Styled.DayNum>

					{eventsForDay.map((event, index) => (
						<Styled.Event
							key={index}
							$backgroundColor={event.backgroundColor}
							$color={event.color}
							onClick={() => {
								openModal(ModalName.TOURNAMENT, {
									event,
									eventIndex: events.indexOf(event)
								});
							}}
						>
							<div>{event.name}</div>
						</Styled.Event>
					))}

					<Styled.GameIcons>
						{eventsForDay.some(event => event.gameType === GAME_TYPE.DOTA2) && <DotaTagIcon />}
						{eventsForDay.some(event => event.gameType === GAME_TYPE.CS2) && <CsTagIcon />}
						{eventsForDay.some(event => event.gameType === GAME_TYPE.LOL) && <LolTagIcon />}
					</Styled.GameIcons>
				</Styled.Day>
			);
		}

		return daysArray;
	};

	if (isLoading) {
		return (
			<Styled.Wrapper>
				<Loader />
			</Styled.Wrapper>
		);
	}

	return (
		<Styled.Wrapper>
			<Styled.Container>
				<CalendarHeader
					months={months}
					currentMonth={currentMonth}
					currentYear={currentYear}
					filteredGameType={filteredGameType}
					onPrevMonth={handlePrevMonth}
					onNextMonth={handleNextMonth}
					onFilterClick={handleFilterClick}
				/>

				<MobileCalendarHeader
					months={months}
					currentMonth={currentMonth}
					currentYear={currentYear}
					filteredGameType={filteredGameType}
					onPrevMonth={handlePrevMonth}
					onNextMonth={handleNextMonth}
					onFilterClick={handleFilterClick}
				/>

				<Styled.Grid>
					{renderDaysOfWeek()}
					{renderDays()}
				</Styled.Grid>
			</Styled.Container>
		</Styled.Wrapper>
	);
};

export default TournamentsCalendar;
